import React, { useEffect, useState } from "react";
import { Drawer, Box, Grid, TextField, Typography, IconButton, Button as MuiButton, CircularProgress, InputAdornment } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationPatientSchema } from "../../../schemas/validationSchema.ts";
import { useFeedback } from "../../../contexts/FeedbackContext.tsx";
import pacienteService from "../../../services/pacienteService.ts";
import enderecoService from "../../../services/enderecoService.ts";
import { Paciente } from "../../../models/Paciente.ts";
import { Endereco } from "../../../models/Endereco.ts";
import { formatBrazilPhone } from "../../../utils/phone.util.ts";
import { maskCep } from "../../../utils/address.util.ts";
import { maskCpf } from "../../../utils/cpf.util.ts";
import { maskDate, brDateToIso, isoDateToBr } from "../../../utils/date.util.ts";

interface UpdatePacienteDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (r: any) => void;
  paciente: any;
  readOnly?: boolean;
}

// Mesma receita de cores dos módulos de Hospital/Médico — sem ThemeProvider
// do MUI, estados de label/borda/desabilitado caem no tema claro padrão do
// MUI, sem contraste no fundo escuro. CSS vars do design system em vez de
// tokens de tema.
const fieldSx = {
  "& .MuiInputLabel-root": { color: "var(--ink-faint)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--blue)" },
  "& .MuiInputLabel-root.Mui-disabled": { color: "var(--ink-faint)" },
  "& .MuiOutlinedInput-root": {
    color: "var(--ink)",
    "& fieldset": { borderColor: "var(--border-strong)" },
    "&:hover fieldset": { borderColor: "var(--border-strong)" },
    "&.Mui-focused fieldset": { borderColor: "var(--blue)" },
    "&.Mui-disabled fieldset": { borderColor: "var(--border)" },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "var(--ink-faint)",
    color: "var(--ink-faint)",
  },
  "& .MuiFormHelperText-root": { color: "var(--red)" },
};

const scrollSx = {
  "&::-webkit-scrollbar": { width: 8 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { background: "var(--border-strong)", borderRadius: 4 },
};

const UpdatePacienteDrawer: React.FC<UpdatePacienteDrawerProps> = ({
  isOpen,
  onClose,
  onUpdate,
  paciente,
  readOnly = false,
}) => {
  const { showFeedback } = useFeedback();
  const [isLoading, setIsLoading] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(validationPatientSchema),
  });

  useEffect(() => {
    if (paciente) {
      reset({
        nome: paciente.nome,
        email: paciente.email,
        telefone: formatBrazilPhone(paciente.telefone) || "",
        cpf: paciente.cpf ? maskCpf(paciente.cpf) : "",
        dataNascimento: isoDateToBr(paciente.dataNascimento),
        endereco: {
          cep: paciente.endereco?.cep ? maskCep(paciente.endereco.cep) : "",
          bairro: paciente.endereco?.bairro || "",
          cidade: paciente.endereco?.cidade || "",
          uf: paciente.endereco?.uf || "",
          rua: paciente.endereco?.rua || "",
          numero: paciente.endereco?.numero || "",
        },
      });
    }
  }, [paciente, reset]);

  const handleCepChange = async (value: string) => {
    const masked = maskCep(value);
    setValue("endereco.cep", masked, { shouldValidate: true });
    const raw = masked.replace(/\D/g, "");
    if (raw.length === 8) {
      setBuscandoCep(true);
      await enderecoService.getAddressByZipCode(raw, "endereco", setValue, showFeedback);
      setBuscandoCep(false);
    }
  };

  const onSubmit = async (data: any) => {
    if (readOnly) return onClose();

    const endereco = new Endereco(
      data.endereco?.rua,
      data.endereco?.bairro,
      data.endereco?.cep,
      data.endereco?.numero,
      data.endereco?.cidade,
      data.endereco?.uf
    );
    const dataNascimentoIso = data.dataNascimento ? brDateToIso(data.dataNascimento) : "";
    // Mesmo tratamento do CreatePacienteDrawer: CPF vazio vira null, não "",
    // pra não gravar string vazia no banco quando o usuário limpa o campo.
    const updated = new Paciente(paciente.id, data.nome, data.email, data.telefone, data.cpf || null, endereco, dataNascimentoIso);

    try {
      setIsLoading(true);
      const resp = await pacienteService.update(updated);
      showFeedback(resp.message, "success");
      onUpdate?.(resp);
      onClose();
    } catch (err: any) {
      showFeedback(err.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460 },
          bgcolor: "var(--surface)",
          color: "var(--ink)",
          borderLeft: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Cabeçalho fixo */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2.5, borderBottom: "1px solid var(--border)", flex: "0 0 auto" }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{readOnly ? "Visualizar Paciente" : "Alterar Paciente"}</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "var(--ink-faint)" }} aria-label="Fechar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Corpo com scroll — flex:1 + overflowY:auto, o resto do Drawer fica fixo */}
      <Box
        component="form"
        id="update-paciente-form"
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ flex: 1, overflowY: "auto", px: 3, py: 3, ...scrollSx }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nome do Paciente*" size="small" fullWidth disabled={readOnly} sx={fieldSx}
              {...register("nome")} autoComplete="off" error={!!errors.nome} helperText={errors.nome?.message as string}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="CPF" size="small" fullWidth disabled={readOnly} sx={fieldSx}
              value={watch("cpf") || ""}
              onChange={(e) => setValue("cpf", maskCpf(e.target.value), { shouldValidate: true })}
              error={!!errors.cpf} helperText={errors.cpf?.message as string}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Data de nascimento" placeholder="dd/mm/aaaa" size="small" fullWidth disabled={readOnly} sx={fieldSx}
              value={watch("dataNascimento") || ""}
              onChange={(e) => setValue("dataNascimento", maskDate(e.target.value), { shouldValidate: true })}
              error={!!errors.dataNascimento} helperText={errors.dataNascimento?.message as string}
              InputProps={{
                endAdornment: <InputAdornment position="end"><CalendarTodayOutlinedIcon fontSize="small" sx={{ color: "var(--ink-faint)" }} /></InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Telefone" size="small" fullWidth disabled={readOnly} sx={fieldSx}
              value={watch("telefone") || ""}
              onChange={(e) => setValue("telefone", formatBrazilPhone(e.target.value), { shouldValidate: true })}
              error={!!errors.telefone} helperText={errors.telefone?.message as string}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="E-mail" size="small" fullWidth disabled={readOnly} sx={fieldSx}
              {...register("email")} error={!!errors.email} helperText={errors.email?.message as string}
            />
          </Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--ink)" }}>
              Endereço
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="CEP" size="small" fullWidth sx={fieldSx}
              value={watch("endereco.cep") || ""}
              onChange={(e) => handleCepChange(e.target.value)}
              disabled={readOnly || buscandoCep}
              error={!!(errors.endereco as any)?.cep}
              helperText={(errors.endereco as any)?.cep?.message}
              InputProps={{ endAdornment: buscandoCep ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : undefined }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Bairro" size="small" fullWidth disabled={readOnly} sx={fieldSx} {...register("endereco.bairro")} />
          </Grid>

          <Grid item xs={6}>
            <TextField label="Cidade" size="small" fullWidth disabled={readOnly} sx={fieldSx} {...register("endereco.cidade")} />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="UF" size="small" fullWidth disabled={readOnly} sx={fieldSx} {...register("endereco.uf")}
              inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
            />
          </Grid>

          <Grid item xs={8}>
            <TextField label="Rua" size="small" fullWidth disabled={readOnly} sx={fieldSx} {...register("endereco.rua")} />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Número" size="small" fullWidth disabled={readOnly} sx={fieldSx} {...register("endereco.numero")} />
          </Grid>
        </Grid>
      </Box>

      {/* Rodapé fixo */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, px: 3, py: 2.5, borderTop: "1px solid var(--border)", flex: "0 0 auto" }}>
        <MuiButton onClick={onClose} disabled={isLoading} sx={{ color: "var(--ink-soft)" }}>
          {readOnly ? "Fechar" : "Cancelar"}
        </MuiButton>
        {!readOnly && (
          <MuiButton type="submit" form="update-paciente-form" variant="contained" disabled={isLoading} disableElevation>
            {isLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Salvar Alterações"}
          </MuiButton>
        )}
      </Box>
    </Drawer>
  );
};

export default UpdatePacienteDrawer;
